import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output
} from '@angular/core';
import {
  TeamAvailabilityOverview,
  TeamAvailabilityTimelineSegment
} from '../../domain/collector-availability-overview.model';
import {
  AVAILABILITY_SLIDER_STEP_MINUTES,
  formatMinuteOfDay,
  minuteToPercentage
} from './availability-overview.utils';

@Component({
  selector: 'app-team-availability-list',
  templateUrl: './team-availability-list.component.html',
  styleUrls: ['./team-availability-list.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TeamAvailabilityListComponent {
  @Input() teams: TeamAvailabilityOverview[] = [];
  @Input() selectedFromMinute = 0;
  @Input() selectedToMinute = 1440;
  @Output() teamSelected = new EventEmitter<string>();
  readonly minuteToPercentage = minuteToPercentage;
  readonly bucketMinutes = AVAILABILITY_SLIDER_STEP_MINUTES;
  activeTeam = '';
  activeSegment: TeamAvailabilityTimelineSegment | null = null;

  selectTeam(team: string): void {
    this.teamSelected.emit(team);
  }

  peakFree(team: TeamAvailabilityOverview): number {
    return team.timeline.reduce(
      (peak, segment) =>
        this.isInSelectedRange(segment) ? Math.max(peak, segment.free) : peak,
      0
    );
  }

  isInSelectedRange(segment: TeamAvailabilityTimelineSegment): boolean {
    return (
      segment.toMinute > this.selectedFromMinute &&
      segment.fromMinute < this.selectedToMinute
    );
  }

  segmentOpacity(freeRatio: number): number {
    const ratio = Math.max(0, Math.min(1, freeRatio));
    return ratio === 0 ? 0 : 0.28 + Math.sqrt(ratio) * 0.72;
  }

  segmentTitle(segment: TeamAvailabilityTimelineSegment): string {
    const period = `${formatMinuteOfDay(
      segment.fromMinute
    )}–${formatMinuteOfDay(segment.toMinute)}`;
    return [
      period,
      `${segment.free} free for full slot`,
      `${segment.busy} busy during slot`,
      `${segment.unavailable} unavailable during slot`,
      `${segment.missing} missing`
    ].join(' · ');
  }

  showSegment(team: string, segment: TeamAvailabilityTimelineSegment): void {
    this.activeTeam = team;
    this.activeSegment = segment;
  }

  hideSegment(team: string): void {
    if (this.activeTeam === team) {
      this.activeTeam = '';
      this.activeSegment = null;
    }
  }

  currentSegment(team: string): TeamAvailabilityTimelineSegment | null {
    return this.activeTeam === team ? this.activeSegment : null;
  }

  trackByTeam(_: number, team: TeamAvailabilityOverview): string {
    return team.team;
  }

  trackBySegment(_: number, segment: TeamAvailabilityTimelineSegment): string {
    return `${segment.fromMinute}-${segment.toMinute}`;
  }
}
